import { Call, Close } from '@poli-ink/react';
import { Button, Card, CardBody } from '@polichat/flamboyant';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import store from 'src/store';
import { didntReply } from 'src/store/modules/chat/actions';
import { useFlags } from 'flagsmith/react';
import { customUseFlags } from 'src/hooks/customUseFlags';

interface LastMessageReceived {
  lastMessage: {
    id: number;
    dir: string;
    dtm: string;
    channel_id: number;
    contact_id: number;
    cuid: string;
    contact: {
      id: number;
      name: string;
      picture: string;
    };
    event: string;
  };
  toContactList: boolean;
  didntReplied: boolean;
  contact: {
    id: number;
    name: string;
    picture: string;
  };
}

export function CallAfterInactivity({
  lastMessage,
  toContactList,
  didntReplied,
  contact,
}: LastMessageReceived) {
  const TIMEOUT_TO_CALL_INACTIVITY =
    Number(process.env?.REACT_APP_TIMEOUT_TO_CALL_INACTIVITY) ?? 5;
  const [shouldShow, setShouldShow] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const flag = useFlags(['call_after_inactivity']);
  const call_after_inactivity = customUseFlags(flag.call_after_inactivity);

  const customer_id = useSelector(
    (state: any) => state.general?.current_customer_id
  );
  const user_id = useSelector((state: any) => state.general?.user.id);

  const channelsList = useSelector(
    (state: any) => state.chat?.config?.general?.channels
  );

  // Verificar qual é o canal ativo
  let channelBinded = channelsList.find(
    (channel: { id: number }) => channel?.id === lastMessage?.channel_id
  );

  const handleCloseOrCalled = useCallback(async () => {
    if (lastMessage?.contact_id) {
      store.dispatch(didntReply(lastMessage.contact_id));
    }
  }, [lastMessage?.contact_id]);

  const handleClickVox = useCallback(async () => {
    window.open(
      `${process.env?.REACT_APP_VOX_URL}?contactID=${contact?.id}&customerID=${customer_id}&userID=${user_id}&contactName=${contact?.name}&contactPicture=${contact?.picture}`,
      'popup',
      'width=700, height=500'
    );
    handleCloseOrCalled();
  }, [handleCloseOrCalled, lastMessage]);

  // Função para verificar se deve mostrar a sugestão de ligação
  const checkInactivity = () => {
    // Verificar tem dtm, se é whatsapp ou waba se é uma mensagem ativa e se é do tipo message
    if (
      !lastMessage?.dtm ||
      lastMessage?.cuid.substring(0, 2) !== 's-' ||
      !['whatsapp', 'waba'].includes(channelBinded?.type) ||
      lastMessage?.dir !== 'o' ||
      lastMessage?.event !== 'message'
    ) {
      setShouldShow(false);
      return;
    }

    const lastMessageReceivedDate = new Date(Number(lastMessage.dtm) * 1000);
    const currentDate = new Date();

    // Verificar se é o mesmo dia
    const isSameDay =
      currentDate.getFullYear() === lastMessageReceivedDate.getFullYear() &&
      currentDate.getMonth() === lastMessageReceivedDate.getMonth() &&
      currentDate.getDate() === lastMessageReceivedDate.getDate();

    // Se não for do mesmo dia, não mostrar a sugestão de ligação
    if (!isSameDay) {
      setShouldShow(false);
      return;
    }

    // Verificar a diferença de tempo entre a última mensagem e o tempo atual
    const timeDifference =
      currentDate.getTime() - lastMessageReceivedDate.getTime();
    const minutesDifference = Math.floor(timeDifference / (1000 * 60));

    const newShouldShow = minutesDifference >= TIMEOUT_TO_CALL_INACTIVITY;

    if (newShouldShow !== shouldShow) {
      setShouldShow(newShouldShow);
    }
  };

  // Effect para verificar periodicamente a inatividade
  useEffect(() => {
    // Verifica se deve mostrar a sugestão de ligação
    checkInactivity();

    // Configura o intervalo para verificar a cada 30 segundos
    const interval = setInterval(checkInactivity, 10000);

    // Limpa o intervalo quando o componente for desmontado ou as dependências mudarem
    return () => clearInterval(interval);
  }, [lastMessage?.dtm, channelBinded?.type, lastMessage?.dir]);

  // Effect para controlar a animação
  useEffect(() => {
    if (!didntReplied) {
      setShouldShow(false);
      return;
    }

    if (shouldShow) {
      // Pequeno delay para permitir que o componente seja renderizado antes da animação
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [shouldShow, didntReplied]);

  if (!call_after_inactivity || !shouldShow) {
    return <></>;
  }

  return (
    <Card
      className={`wrapper-call-after-inactivity ${
        isAnimating && !toContactList ? 'show' : ''
      }`}
      style={
        toContactList && {
          position: 'absolute',
          bottom: '0px',
          left: 0,
          width: '100%',
          border: '1px solid var(--border-color)',
          height: 'auto',
          padding: '0px',
        }
      }
    >
      <CardBody className="call-after-inactivity-body">
        <span className="text-sm/[15.4px] font-normal text-start">
          Sem resposta há 5 minutos. Que tal uma ligação?
        </span>
        <Button
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            handleClickVox();
          }}
          className="call-after-inactivity-button"
        >
          <Call width={16} height={16} />
          <p>Ligar</p>
        </Button>
        {!toContactList && (
          <Button
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleCloseOrCalled();
            }}
            size="icon"
            className="x-button-call-after-inactivity"
          >
            <Close width={11} height={11} />
          </Button>
        )}
      </CardBody>
    </Card>
  );
}
